import { Fragment, type ReactNode } from 'react'
import { cn } from './cn'

const ARABIC = /[؀-ۿ]/
// A run of Latin words/digits ("Node.js", "Ubuntu 24.04", "server1.rimalhost.sa", "1,200", "bpanel-install
// --device"), always ending on a letter or digit so sentence punctuation stays outside the isolate. A comma
// joins only thousands ("1,200"). A word may start with "-" or "--" after a space (a CLI flag).
const WORD = String.raw`(?:(?<![^\s(])--?)?\.?[A-Za-z0-9](?:(?:[A-Za-z0-9.'’_\-/@+:#]|,(?=\d{3}))*[A-Za-z0-9])?`
const LATIN_RUN = new RegExp(String.raw`((?:© )?${WORD}(?: ${WORD})*)`, 'g')

/**
 * Renders a translated string. Inside Arabic text every Latin/digit run is wrapped in `<bdi dir="ltr">`, so
 * digits stay Latin and left to right and a hostname never jumps across the line. A run of one token
 * (hostname, key, version, flag) also never wraps inside itself, e.g. at the hyphen of vps-dmm.example.com.
 * English passes through. Use it for any translated string that can contain Latin runs:
 * `<Bidi text={t('servers.count', { n })} />`.
 */
export function Bidi({ text, className }: { text: string; className?: string }) {
  if (!ARABIC.test(text)) return className ? <span className={className}>{text}</span> : <>{text}</>
  const parts = text.split(LATIN_RUN)
  return (
    <span className={className}>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <bdi key={i} dir="ltr" className={part.includes(' ') ? undefined : 'whitespace-nowrap'}>
            {part}
          </bdi>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </span>
  )
}

/** Latin content (hostnames, keys, versions, emails) placed inside RTL copy; it never wraps inside itself. */
export function Ltr({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <bdi dir="ltr" className={cn('whitespace-nowrap', className)}>
      {children}
    </bdi>
  )
}
